// The board on the session's kit (Decision sync-engine, api/client.ts sessionKit): the kit keeps the records on this
// device (IndexedDB) as a replica, kept current by the sockets; the board is the core's view `board.snapshot`
// (platform/core/features/board), repainted on every change, this device's unsent writes included.
import type { LiveStatus } from "@hq-org/kit-wasm";
import { type Snapshot, sessionKit } from "@/api";
import { snapshotOf, snapshotParams } from "@/api/kit";

export type KitBoard = { stop: () => void };

/** Watches the board on the session's kit; `onSnapshot` gets it on every change. */
export async function startKitBoard(o: {
	onSnapshot: (s: Snapshot) => void;
	onLive: (s: LiveStatus) => void;
	onError: (message: string) => void;
}): Promise<KitBoard> {
	const k = await sessionKit();
	const watch = (params: Awaited<ReturnType<typeof snapshotParams>>) =>
		k.watch("board.snapshot", params, (v) => {
			if (v.status === "loading") return;
			if (v.status === "error") return o.onError(v.error && "message" in v.error && v.error.message ? v.error.message : JSON.stringify(v.error));
			const parsed = snapshotOf(v.data);
			if (parsed.success) o.onSnapshot(parsed.data);
			else o.onError(`board: ${parsed.error.issues[0]?.path.join(".")} ${parsed.error.issues[0]?.message}`);
		});
	// the session's spaces tell the stub ancestors (space trees); a tree change makes the instance send `rejoin`
	// and the kit redial: once live again, re-read them and re-watch when they changed
	let params = await snapshotParams(k);
	let w = watch(params);
	let was = k.status.live;
	const offStatus = k.onStatus((s) => {
		o.onLive(s.live);
		if (s.live === "live" && was !== "live")
			void snapshotParams(k).then((next) => {
				if (JSON.stringify(next) === JSON.stringify(params)) return;
				params = next;
				w.close();
				w = watch(params);
			});
		was = s.live;
	});
	o.onLive(k.status.live);
	return {
		stop() {
			w.close();
			offStatus();
		},
	};
}
